import { createFileRoute } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Inbox,
  Loader2,
  MessageCircle,
  RefreshCw,
  Send,
  ShieldCheck,
  WalletCards,
  XCircle,
} from 'lucide-react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import {
  useMetaWhatsAppMetrics,
  useMetaWhatsAppStatus,
  useSubmitDefaultMetaWhatsAppTemplates,
  type MetaWhatsAppAlert,
  type MetaWhatsAppTemplate,
  type MetaWhatsAppTemplateComponent,
  type TemplateSubmissionResult,
} from '@/features/super-admin/hooks/use-meta-whatsapp'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/super-admin/meta-whatsapp/')({
  component: MetaWhatsAppPage,
})

const statusLabels: Record<string, string> = {
  APPROVED: 'Aprovado',
  PENDING: 'Em análise',
  REJECTED: 'Rejeitado',
  PAUSED: 'Pausado',
  DISABLED: 'Desativado',
  IN_APPEAL: 'Em recurso',
  PENDING_DELETION: 'Exclusão pendente',
}

const categoryLabels: Record<string, string> = {
  UTILITY: 'Utilidade',
  MARKETING: 'Marketing',
  AUTHENTICATION: 'Autenticação',
}

function MetaWhatsAppPage() {
  const { onMenuToggle } = useMenuToggle()
  const { data, isLoading, isFetching, error, refetch } = useMetaWhatsAppStatus()
  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useMetaWhatsAppMetrics(30)
  const submitTemplates = useSubmitDefaultMetaWhatsAppTemplates()
  const [showSubmissionDetails, setShowSubmissionDetails] = useState(false)

  const approved = data?.templateSummary.APPROVED || 0
  const pending = data?.templateSummary.PENDING || 0
  const rejected = data?.templateSummary.REJECTED || 0
  const unavailable =
    !isLoading &&
    (!data?.configuration.configured || Boolean(error) || Boolean(data?.metaErrors.length))
  const alerts = metrics?.activeAlerts || data?.activeAlerts || []
  const activeAlerts = alerts.filter((alert) => alert.status === 'active').length
  const disabledAlerts = alerts.filter((alert) => alert.status === 'disabled').length
  const pendingTemplateAlerts = alerts.filter((alert) => alert.status === 'pending_template').length
  const estimatedCost = metrics
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: metrics.spend.currency }).format(metrics.spend.estimatedCostBrl)
    : 'R$ 0,00'

  const handleRefresh = () => {
    refetch()
    refetchMetrics()
  }

  const handleSubmitTemplates = async () => {
    setShowSubmissionDetails(true)
    await submitTemplates.mutateAsync()
  }

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Meta WhatsApp"
        subtitle="Configuração da Cloud API, telefone, webhook e modelos de mensagem"
        onMenuToggle={onMenuToggle}
      />

      <div className="flex-1 overflow-auto p-4 md:p-6 max-w-7xl mx-auto w-full">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={data?.configuration.configured && !error ? 'APPROVED' : unavailable ? 'REJECTED' : 'PENDING'}>
              {data?.configuration.configured && !error ? 'Conectado' : unavailable ? 'Atenção' : 'Verificando'}
            </StatusPill>
            {data?.lastCheckedAt && (
              <span className="text-sm text-gray-500">
                Atualizado em {new Date(data.lastCheckedAt).toLocaleString('pt-BR')}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <a
              href="https://business.facebook.com/latest/whatsapp_manager/message_templates"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Meta
            </a>
            <button
              onClick={handleRefresh}
              disabled={isFetching || metricsLoading}
              className="inline-flex items-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
              Atualizar
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <EmptyState
            icon={<AlertTriangle className="w-8 h-8" />}
            title="Não foi possível consultar a Meta"
            description={error instanceof Error ? error.message : 'A API retornou erro ao buscar os dados do WhatsApp.'}
          />
        ) : data ? (
          <div className="space-y-6">
            {data.metaErrors.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-sm text-amber-800">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">A Meta recusou a consulta com as credenciais atuais.</div>
                  <div className="mt-1 space-y-1">
                    {data.metaErrors.map((metaError) => (
                      <div key={metaError}>{metaError}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard label="Templates" value={data.templates.length} detail="Total no WABA" />
              <MetricCard label="Aprovados" value={approved} detail="Prontos para envio" tone="success" />
              <MetricCard label="Em análise" value={pending} detail="Aguardando Meta" tone="warning" />
              <MetricCard label="Rejeitados" value={rejected} detail="Precisam revisão" tone={rejected > 0 ? 'danger' : 'neutral'} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard
                label="Recebidas"
                value={metrics?.totals.receivedMessages ?? 0}
                detail="Mensagens inbound em 30 dias"
                icon={<Inbox className="w-4 h-4" />}
              />
              <MetricCard
                label="Enviadas"
                value={metrics?.totals.sentMessages ?? 0}
                detail="Mensagens outbound em 30 dias"
                icon={<Send className="w-4 h-4" />}
              />
              <MetricCard
                label="Janelas ativas"
                value={metrics?.totals.activeConversationWindows ?? 0}
                detail="Conversas dentro das 24h"
                icon={<Activity className="w-4 h-4" />}
                tone="success"
              />
              <MetricCard
                label="Custo estimado"
                value={estimatedCost}
                detail={metrics?.spend.ratesConfigured ? 'Estimativa por conversa' : 'Configure tarifas no ambiente'}
                icon={<WalletCards className="w-4 h-4" />}
                tone={metrics?.spend.ratesConfigured ? 'warning' : 'neutral'}
              />
            </div>

            <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-teal-600" />
                  <div>
                    <h2 className="font-semibold text-gray-900">Alertas WhatsApp</h2>
                    <p className="text-sm text-gray-500">
                      {activeAlerts} ativos, {pendingTemplateAlerts} aguardando template e {disabledAlerts} desativado
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleSubmitTemplates}
                  disabled={submitTemplates.isPending || !data.configuration.configured}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {submitTemplates.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submeter templates
                </button>
              </div>

              <div className="divide-y divide-gray-100">
                {alerts.map((alert) => (
                  <AlertRow key={alert.key} alert={alert} />
                ))}
              </div>

              {submitTemplates.data && showSubmissionDetails && (
                <TemplateSubmissionPanel
                  submitted={submitTemplates.data.submitted}
                  skipped={submitTemplates.data.skipped}
                  failed={submitTemplates.data.failed}
                  results={submitTemplates.data.results}
                  onClose={() => setShowSubmissionDetails(false)}
                />
              )}

              {submitTemplates.error && (
                <div className="px-6 py-4 bg-red-50 border-t border-red-100 text-sm text-red-700">
                  {submitTemplates.error instanceof Error ? submitTemplates.error.message : 'Falha ao submeter templates'}
                </div>
              )}
            </section>

            <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-teal-600" />
                <div>
                  <h2 className="font-semibold text-gray-900">Parâmetros da integração</h2>
                  <p className="text-sm text-gray-500">IDs operacionais, segredos mascarados e estado do webhook</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
                <InfoGroup
                  title="Cloud API"
                  items={[
                    ['Provider', data.configuration.provider],
                    ['Graph API', data.configuration.graphApiVersion],
                    ['WABA ID', data.configuration.businessAccountId || '-'],
                    ['Phone Number ID', data.configuration.phoneNumberId || '-'],
                    ['Access token', data.configuration.accessToken.configured ? data.configuration.accessToken.masked || 'Configurado' : 'Não configurado'],
                  ]}
                />
                <InfoGroup
                  title="Conta e número"
                  items={[
                    ['Conta', data.businessAccount?.name || '-'],
                    ['Moeda', data.businessAccount?.currency || '-'],
                    ['Telefone', data.phoneNumber?.display_phone_number || '-'],
                    ['Nome verificado', data.phoneNumber?.verified_name || '-'],
                    ['Qualidade', data.phoneNumber?.quality_rating || '-'],
                    ['Throughput', data.phoneNumber?.throughput?.level || '-'],
                  ]}
                />
                <InfoGroup
                  title="Webhook"
                  items={[
                    ['Callback', data.configuration.webhookCallbackPath],
                    ['Campo inscrito', data.configuration.subscribedWebhookField],
                    ['Verify token', data.configuration.webhookVerifyToken.configured ? data.configuration.webhookVerifyToken.masked || 'Configurado' : 'Não configurado'],
                    ['App secret', data.configuration.appSecret.configured ? data.configuration.appSecret.masked || 'Configurado' : 'Não configurado'],
                    ['Assinatura', data.configuration.signatureVerificationEnabled ? 'Ativa' : 'Inativa'],
                    ['App inscrito', data.subscribedApps.map((app) => app.name).filter(Boolean).join(', ') || '-'],
                  ]}
                />
              </div>

              {data.configuration.missingConfig.length > 0 && (
                <div className="px-6 py-4 bg-amber-50 border-t border-amber-100 text-sm text-amber-800">
                  Variáveis ausentes: {data.configuration.missingConfig.join(', ')}
                </div>
              )}
            </section>

            <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-teal-600" />
                  <div>
                    <h2 className="font-semibold text-gray-900">Templates da Meta</h2>
                    <p className="text-sm text-gray-500">Status de aprovação, categoria final e qualidade</p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-6 py-3">Template</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3">Categoria</th>
                      <th className="px-6 py-3">Qualidade</th>
                      <th className="px-6 py-3">Mensagem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.templates.map((template) => (
                      <TemplateRow key={`${template.id}-${template.language}`} template={template} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function MetricCard({
  label,
  value,
  detail,
  icon,
  tone = 'neutral',
}: {
  label: string
  value: number | string
  detail: string
  icon?: ReactNode
  tone?: 'neutral' | 'success' | 'warning' | 'danger'
}) {
  const toneClass = {
    neutral: 'text-gray-900 bg-gray-100',
    success: 'text-green-700 bg-green-100',
    warning: 'text-amber-700 bg-amber-100',
    danger: 'text-red-700 bg-red-100',
  }[tone]

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
        {icon}
        {label}
      </div>
      <div className={cn('mt-3 inline-flex min-w-12 h-10 items-center justify-center rounded-lg px-3 text-2xl font-bold', toneClass)}>
        {value}
      </div>
      <div className="mt-3 text-sm text-gray-500">{detail}</div>
    </div>
  )
}

function AlertRow({ alert }: { alert: MetaWhatsAppAlert }) {
  const statusLabel = {
    active: 'Ativo',
    pending_template: 'Aguardando template',
    disabled: 'Desativado',
  }[alert.status]
  const statusTone =
    alert.status === 'active'
      ? 'APPROVED'
      : alert.status === 'pending_template'
        ? 'PENDING'
        : 'DISABLED'

  return (
    <div className="px-6 py-4 grid grid-cols-1 lg:grid-cols-[minmax(220px,1fr)_140px_minmax(260px,1.2fr)_minmax(220px,1fr)] gap-3 lg:items-center">
      <div>
        <div className="font-medium text-gray-900">{alert.label}</div>
        <div className="text-xs text-gray-500 mt-1">{alert.channel}</div>
      </div>
      <div>
        <StatusPill status={statusTone}>{statusLabel}</StatusPill>
      </div>
      <div className="text-sm text-gray-600">{alert.trigger}</div>
      <div className="text-sm text-gray-600">{alert.delivery}</div>
    </div>
  )
}

function TemplateSubmissionPanel({
  submitted,
  skipped,
  failed,
  results,
  onClose,
}: {
  submitted: number
  skipped: number
  failed: number
  results: TemplateSubmissionResult[]
  onClose: () => void
}) {
  return (
    <div className="border-t border-gray-100 bg-gray-50">
      <div className="px-6 py-4 flex items-center justify-between gap-4">
        <div>
          <div className="font-medium text-gray-900">Submissão de templates</div>
          <div className="text-sm text-gray-500">
            {submitted} submetidos, {skipped} já existentes, {failed} com falha
          </div>
        </div>
        <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-gray-900">
          Ocultar
        </button>
      </div>
      <div className="px-6 pb-4 overflow-x-auto">
        <table className="w-full text-sm bg-white border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Template</th>
              <th className="px-4 py-3">Resultado</th>
              <th className="px-4 py-3">Detalhe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {results.map((result) => (
              <tr key={`${result.name}-${result.language}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{result.name}</div>
                  <div className="text-xs text-gray-500">{result.language}</div>
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={result.success ? (result.skipped ? 'PENDING' : 'APPROVED') : 'REJECTED'}>
                    {result.skipped ? 'Já existia' : result.success ? 'Submetido' : 'Falhou'}
                  </StatusPill>
                </td>
                <td className="px-4 py-3 text-gray-600 max-w-xl">
                  {result.metaError || result.message || result.status || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function InfoGroup({ title, items }: { title: string; items: Array<[string, string]> }) {
  return (
    <div className="p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">{title}</h3>
      <dl className="space-y-3">
        {items.map(([label, value]) => (
          <div key={label} className="flex items-start justify-between gap-4">
            <dt className="text-sm text-gray-500">{label}</dt>
            <dd className="text-sm font-medium text-gray-900 text-right break-all">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function TemplateRow({ template }: { template: MetaWhatsAppTemplate }) {
  const body = getBodyText(template.components)
  const status = template.status
  const category = template.category
  const quality = template.quality_score?.score || '-'
  const rejection = template.rejected_reason && template.rejected_reason !== 'NONE' ? template.rejected_reason : null

  return (
    <tr className="align-top hover:bg-gray-50">
      <td className="px-6 py-4">
        <div className="font-medium text-gray-900 break-all">{template.name}</div>
        <div className="text-xs text-gray-500 mt-1">{template.language}</div>
        <div className="text-xs text-gray-400 mt-1 break-all">{template.id}</div>
      </td>
      <td className="px-6 py-4">
        <StatusPill status={status}>{statusLabels[status] || status}</StatusPill>
        {rejection && <div className="mt-2 text-xs text-red-600 max-w-44">{rejection}</div>}
      </td>
      <td className="px-6 py-4">
        <span
          className={cn(
            'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
            category === 'UTILITY' && 'bg-teal-100 text-teal-700',
            category === 'MARKETING' && 'bg-amber-100 text-amber-700',
            category === 'AUTHENTICATION' && 'bg-blue-100 text-blue-700',
            !['UTILITY', 'MARKETING', 'AUTHENTICATION'].includes(category) && 'bg-gray-100 text-gray-700',
          )}
        >
          {categoryLabels[category] || category}
        </span>
      </td>
      <td className="px-6 py-4 text-gray-700">{quality}</td>
      <td className="px-6 py-4 min-w-80 max-w-xl text-gray-700">
        <p className="leading-relaxed">{body}</p>
      </td>
    </tr>
  )
}

function StatusPill({ status, children }: { status: string; children: ReactNode }) {
  const styles =
    status === 'APPROVED'
      ? 'bg-green-100 text-green-700'
      : status === 'PENDING'
        ? 'bg-amber-100 text-amber-700'
        : status === 'REJECTED' || status === 'DISABLED'
          ? 'bg-red-100 text-red-700'
          : 'bg-gray-100 text-gray-700'

  const Icon =
    status === 'APPROVED' ? CheckCircle2 : status === 'PENDING' ? Clock3 : status === 'REJECTED' || status === 'DISABLED' ? XCircle : AlertTriangle

  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', styles)}>
      <Icon className="w-3.5 h-3.5" />
      {children}
    </span>
  )
}

function EmptyState({
  icon,
  title,
  description,
}: {
  icon: ReactNode
  title: string
  description: string
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center">{icon}</div>
      <h2 className="mt-4 text-lg font-semibold text-gray-900">{title}</h2>
      <p className="mt-2 text-sm text-gray-500 max-w-xl mx-auto">{description}</p>
    </div>
  )
}

function getBodyText(components?: MetaWhatsAppTemplateComponent[]) {
  return components?.find((component) => component.type === 'BODY')?.text || 'Sem corpo de mensagem'
}
